import { Box, Card, Chip, Link, Stack, Typography } from "@mui/joy";
import {
  ArrowUpRight,
  Briefcase,
  House,
  MapPin,
  Phone,
  User,
} from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";
import type { Task } from "../../types/tasks";

const mapsUrl = (address: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
const phoneUrl = (phone: string) => `tel:${phone.replace(/[^+0-9,;*#]/g, "")}`;

export function TaskRelatedRecords({
  task,
  onNavigate,
}: {
  task: Task;
  onNavigate?: () => void;
}) {
  // Older servers can still provide the existing record links during rollout.
  const context = task.related_context ?? {
    job: task.related_job
      ? {
          id: task.related_job,
          name: task.related_job_title || `Job #${task.related_job}`,
          reference: "",
          address: "",
        }
      : null,
    opportunity: task.related_opportunity
      ? {
          id: task.related_opportunity,
          name:
            task.related_opportunity_name ||
            `Opportunity #${task.related_opportunity}`,
          reference: "",
          address: "",
        }
      : null,
    contacts: [],
  };
  if (!context.job && !context.opportunity && !context.contacts.length)
    return null;
  return (
    <Card
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, gap: 2, minWidth: 0 }}
    >
      <Typography level="title-md">Related records</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
          gap: 3,
          alignItems: "start",
        }}
      >
        {(
          [
            ["job", context.job],
            ["opportunity", context.opportunity],
          ] as const
        ).map(
          ([kind, record]) =>
            record && (
              <Stack key={kind} spacing={0.75}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: "text.tertiary" }}
                >
                  {kind === "job" ? (
                    <House size={18} />
                  ) : (
                    <Briefcase size={18} />
                  )}
                  <Typography level="body-xs">
                    {kind === "job" ? "Job" : "Opportunity"}
                    {record.reference ? ` · ${record.reference}` : ""}
                  </Typography>
                </Stack>
                <Link
                  component={RouterLink}
                  to={
                    kind === "job"
                      ? `/operations/jobs/${record.id}`
                      : `/crm/opportunities/${record.id}`
                  }
                  onClick={onNavigate}
                  endDecorator={<ArrowUpRight size={18} />}
                  sx={{
                    fontWeight: 600,
                    alignSelf: "flex-start",
                    minHeight: 44,
                    overflowWrap: "anywhere",
                  }}
                >
                  {record.name}
                </Link>
                {record.address ? (
                  <Address label="Property address" address={record.address} />
                ) : (
                  task.related_context && (
                    <Typography level="body-sm" textColor="text.tertiary">
                      No property address on file
                    </Typography>
                  )
                )}
              </Stack>
            ),
        )}
        {context.contacts.map((contact) => (
          <Box
            key={contact.id}
            sx={{
              minWidth: 0,
              p: 2,
              bgcolor: "background.level1",
              borderRadius: "sm",
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <User size={18} />
                <Typography level="body-xs" textColor="text.tertiary">
                  {contact.sources.join(" · ") || "Contact"}
                </Typography>
              </Stack>
              <Link
                component={RouterLink}
                to={`/crm/contacts/${contact.id}`}
                onClick={onNavigate}
                endDecorator={<ArrowUpRight size={18} />}
                sx={{
                  alignSelf: "flex-start",
                  fontWeight: 600,
                  minHeight: 44,
                  overflowWrap: "anywhere",
                }}
              >
                {contact.name}
              </Link>
              <Stack
                direction="row"
                sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}
              >
                {contact.phone && !contact.do_not_call ? (
                  <Link
                    href={phoneUrl(contact.phone)}
                    startDecorator={<Phone size={18} />}
                    aria-label={`Call ${contact.name} at ${contact.phone}`}
                    sx={{ minHeight: 44 }}
                  >
                    {contact.phone}
                  </Link>
                ) : (
                  <Typography
                    level="body-sm"
                    startDecorator={<Phone size={18} />}
                    textColor="text.secondary"
                  >
                    {contact.phone || "No phone number on file"}
                  </Typography>
                )}
                {contact.do_not_call && (
                  <Chip size="sm" variant="soft" color="warning">
                    Do not call
                  </Chip>
                )}
              </Stack>
              {contact.address ? (
                <Address label="Contact address" address={contact.address} />
              ) : (
                <Typography level="body-sm" textColor="text.tertiary">
                  No contact address on file
                </Typography>
              )}
            </Stack>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

function Address({ label, address }: { label: string; address: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography level="body-xs" textColor="text.tertiary">
        {label}
      </Typography>
      <Link
        href={mapsUrl(address)}
        target="_blank"
        rel="noopener noreferrer"
        startDecorator={<MapPin size={18} />}
        aria-label={`Get directions to ${address}`}
        sx={{
          alignSelf: "flex-start",
          minHeight: 44,
          alignItems: "center",
          overflowWrap: "anywhere",
          "& > span": { flexShrink: 0 },
        }}
      >
        {address}
      </Link>
    </Stack>
  );
}
